import type { QueryClient, QueryKey } from '@tanstack/react-query';

import type { UiAuthUser } from '../schemas/users.schema';
import type { Maybe } from '../types/maybe.types';

import { pickOrgMembership } from '../context/OrganizationProvider/pickOrgMembership';
import { queryKeys } from '../queryKeys';

/**
 * Everything a `use*Socket` hook keeps fresh from a push, as the query families it
 * touches. A socket that was down missed those pushes — Cloud Run cuts every WebSocket at
 * the request timeout, and a laptop lid or a dropped Wi-Fi does the same — so after the
 * socket comes BACK these are refreshed once instead.
 *
 * Kept next to the hooks' own lists on purpose: a new socket event that invalidates a
 * family nobody adds here is a reconnect that silently shows stale data.
 *
 * - `useNotificationSocket`: the notification list, the unread badge (the push
 *   increments it locally, so only a refetch can recount what was missed) and the
 *   notification-center templates;
 * - `useScheduleSocket` / `useScheduleNoteSocket`: the calendar and its notes;
 * - `useInvitationSocket`: the pending invitation (pushed with `setQueryData`);
 * - `useOrganizationUsersSocket`: users, trainers, clients and members, infinite lists
 *   included, and the organization switcher;
 * - `useTermsOfUseSocket`: the org Terms of Use and its verification status.
 */
export const SOCKET_CATCH_UP_QUERY_KEYS: readonly QueryKey[] = [
  queryKeys.notifications.fetchInfiniteNotifications._def,
  queryKeys.notifications.unreadCount._def,
  queryKeys.notificationCenter.fetchNotificationCenterTemplates._def,
  queryKeys.schedule.fetchSchedule._def,
  queryKeys.scheduleNote.fetchScheduleNotes._def,
  queryKeys.invitations.fetchInvitationByUserId._def,
  queryKeys.users.fetchUsers._def,
  queryKeys.trainers.fetchOrganizationTrainers._def,
  queryKeys.trainers.fetchInfiniteOrganizationTrainers._def,
  queryKeys.organizationClients.fetchOrganizationClients._def,
  queryKeys.organizationClients.fetchInfiniteOrganizationClients._def,
  queryKeys.organizationMembers.fetchOrganizationMembers._def,
  queryKeys.organizations.fetchInfiniteSwitcherOrganizations._def,
  queryKeys.termsOfUse.fetchOrgTermsOfUse._def,
  queryKeys.termsOfUse.fetchOrgTermsOfUseStatus._def,
];

export type SocketCatchUpDeps = {
  clearOrganization: () => void;
  /**
   * The organization selected right now (`undefined` in personal or corp context). Must
   * be LIVE — read when called, not when the deps were built: it is read on both sides of
   * the `/auth/me` await.
   */
  getCurrentOrganizationId: () => string | undefined;
  /** The socket instance that reconnected is still this session's. */
  isLive: () => boolean;
  queryClient: QueryClient;
  refetchCurrentUser: () => Promise<Maybe<UiAuthUser>>;
};

/**
 * Catch up on what a disconnected socket missed. Called on a RE-connect only — the first
 * connect of an instance has nothing to catch up on.
 *
 * Invalidation, not replay: every family is marked stale (inactive ones included, so the
 * next screen that mounts one refetches it) and the active ones refetch. No toast, sound or
 * badge increment is replayed — those belong to the push that was missed, not to now.
 *
 * `organization:disconnected` needs more than a list refresh: the live handler re-reads
 * `/auth/me` and leaves the organization that dropped this account. Here the event itself
 * is gone, so the same question is asked of the refreshed account instead — a regular
 * account whose membership in the selected organization is no longer ACTIVE leaves it. A
 * platform account never holds a membership (P1) and selects any organization in scope,
 * so it is never cleared on that ground.
 *
 * The selection is read BEFORE `/auth/me` is awaited, and the clear happens only if it is
 * still that same organization afterwards (review 2026-10-09, #4). The answer describes
 * the account as it was when the request left; a user who accepted an invitation and was
 * switched into the new organization while it was in flight would otherwise be dropped
 * out of it by an answer that predates the membership.
 */
export async function catchUpMissedSocketEvents({
  clearOrganization,
  getCurrentOrganizationId,
  isLive,
  queryClient,
  refetchCurrentUser,
}: SocketCatchUpDeps) {
  if (!isLive()) return;

  for (const queryKey of SOCKET_CATCH_UP_QUERY_KEYS) {
    void queryClient.invalidateQueries({ exact: false, queryKey });
  }

  const organizationId = getCurrentOrganizationId();
  let user: Maybe<UiAuthUser>;
  try {
    user = await refetchCurrentUser();
  } catch {
    // The auth layer owns a failed `/auth/me` (renewal, sign-out, toast); the catch-up has
    // nothing to add to it and must not leave an unhandled rejection behind.
    return;
  }
  if (!isLive() || !user || user.isPlatformUser) return;

  if (!organizationId) return;
  // The selection moved while the answer was on the wire: that answer is not about it.
  if (getCurrentOrganizationId() !== organizationId) return;
  const membership = pickOrgMembership(user.organizationUsers, organizationId);
  if (membership?.status !== 'ACTIVE') clearOrganization();
}

/** The quiet period after a reconnect before the catch-up runs. */
export const SOCKET_CATCH_UP_DELAY_MS = 3_000;
/** Up to this much more, at random, so a whole fleet does not catch up in lockstep. */
export const SOCKET_CATCH_UP_JITTER_MS = 2_000;

/**
 * One socket instance's catch-up, coalesced (review 2026-10-09, #3).
 *
 * A catch-up is not cheap: every family above is invalidated (active ones refetch, an
 * infinite list refetches every page) and `/auth/me` is re-read behind a forced token
 * refresh. Reconnects come in bursts — an expired cookie produces TWO `connect`s for one
 * episode (socket.io's automatic reconnect, refused by the gateway, then the recovery's
 * explicit one), a flapping link one every few seconds, and a backend deploy drops every
 * socket of every client at the same instant against one fresh instance. So:
 *
 * - a reconnect does not run the catch-up, it REQUESTS one, which runs after a quiet
 *   period of `delayMs` plus up to `jitterMs` at random;
 * - requests while one is pending are the same catch-up (it has not read anything yet);
 * - single-flight: a request while one is RUNNING may have missed what that run already
 *   read, so it books exactly one trailing run, again after the quiet period.
 *
 * Starts are therefore at least `delayMs` apart, however often the link flaps.
 */
export function createSocketCatchUpScheduler(
  run: () => Promise<void>,
  {
    delayMs = SOCKET_CATCH_UP_DELAY_MS,
    jitterMs = SOCKET_CATCH_UP_JITTER_MS,
    random = Math.random,
  }: { delayMs?: number; jitterMs?: number; random?: () => number } = {},
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  let trailing = false;
  let disposed = false;

  const arm = () => {
    timer = setTimeout(
      () => {
        void fire();
      },
      delayMs + Math.floor(random() * jitterMs),
    );
  };

  const fire = async () => {
    timer = null;
    if (disposed) return;
    running = true;
    try {
      await run();
    } catch {
      // `catchUpMissedSocketEvents` swallows what it owns; nothing else is ours to report.
    } finally {
      running = false;
      if (trailing && !disposed) {
        trailing = false;
        arm();
      }
    }
  };

  return {
    request() {
      if (disposed || timer) return;
      if (running) {
        trailing = true;
        return;
      }
      arm();
    },
    dispose() {
      disposed = true;
      trailing = false;
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}
